import { AnimationFactory } from "../types";

type Spore = {
    x: number;
    y: number;
    vx: number;
    vy: number;
    life: number; // 1 -> 0
    size: number;
    drift: number;
};

type Vein = {
    points: { x: number; y: number }[];
    life: number; // 1 -> 0
    width: number;
};

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const easeOutCubic = (t: number) => {
    t = clamp01(t);
    return 1 - Math.pow(1 - t, 3);
};

const easeOutQuad = (t: number) => {
    t = clamp01(t);
    return 1 - (1 - t) * (1 - t);
};

const easeInOutQuad = (t: number) => {
    t = clamp01(t);
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
};

const makeRng = (seed: number) => {
    let s = seed >>> 0;
    return () => {
        s ^= s << 13;
        s ^= s >>> 17;
        s ^= s << 5;
        return (s >>> 0) / 4294967296;
    };
};

const TAU = Math.PI * 2;

export const createPlagueAnimation: AnimationFactory = ({
                                                            ctx,
                                                            canvas,
                                                            startX,
                                                            startY,
                                                            endX,
                                                            endY,
                                                            startTime,
                                                            scale,
                                                        }) => {
    const W = canvas.width;
    const H = canvas.height;

    const cx = endX;
    const cy = endY - canvas.height * 0.06;

    const s = scale ?? 1;

    const maxR = 0.58 * Math.min(W, H);
    const coreMax = Math.min(W, H) * 0.085 * s;

    const FLIGHT_MS = 300;
    const BLOOM_MS = 1300;
    const TOTAL_MS = FLIGHT_MS + BLOOM_MS;

    const rng = makeRng(
        (startTime | 0) ^
        ((startX * 73856093) | 0) ^
        ((startY * 19349663) | 0) ^
        ((endX * 83492791) | 0)
    );

    const spores: Spore[] = [];
    const veins: Vein[] = [];
    let spawned = false;

    const spawnSpores = () => {
        spores.length = 0;
        const count = 110;

        for (let i = 0; i < count; i++) {
            const a = rng() * TAU;
            const spd = lerp(18, 135, rng()) * s;

            spores.push({
                x: cx + (rng() - 0.5) * 20 * s,
                y: cy + (rng() - 0.5) * 20 * s,
                vx: Math.cos(a) * spd,
                vy: Math.sin(a) * spd * 0.65 - lerp(4, 18, rng()) * s,
                life: lerp(0.55, 1.0, rng()),
                size: lerp(2.5, 8.5, rng()) * s,
                drift: lerp(0.5, 1.6, rng()),
            });
        }
    };

    const spawnVein = (count: number) => {
        for (let i = 0; i < count; i++) {
            const a = rng() * TAU;
            const len = lerp(maxR * 0.18, maxR * 0.6, rng());
            const steps = 7 + Math.floor(rng() * 6);

            const pts: { x: number; y: number }[] = [{ x: cx, y: cy }];

            for (let k = 1; k <= steps; k++) {
                const t = k / steps;
                const bend = (rng() - 0.5) * 0.9;
                const aa = a + bend * t;

                const baseX = cx + Math.cos(aa) * len * t;
                const baseY = cy + Math.sin(aa) * len * t;

                const jitter = ((1 - t) * 12 + 6) * s;
                pts.push({
                    x: baseX + (rng() - 0.5) * jitter,
                    y: baseY + (rng() - 0.5) * jitter,
                });
            }

            veins.push({
                points: pts,
                life: lerp(0.55, 1.0, rng()),
                width: lerp(1.2, 3.8, rng()) * s,
            });
        }
    };

    const updateSpores = (dt: number, t01: number) => {
        for (const p of spores) {
            p.life -= dt * 0.33;

            const wobbleX = Math.sin((1 - p.life) * 11 + p.drift * 3.1) * 7 * s;
            const wobbleY = Math.cos((1 - p.life) * 8 + p.drift * 2.3) * 3 * s;

            p.vx *= 1 - dt * 0.28;
            p.vy *= 1 - dt * 0.18;

            // легке “розповзання” від центру
            const dx = p.x - cx;
            const dy = p.y - cy;
            const d = Math.hypot(dx, dy) || 1;
            const spread = lerp(6, 22, t01) * s;

            p.vx += (dx / d) * spread * dt;
            p.vy += (dy / d) * spread * 0.65 * dt;

            // міазми трохи підіймаються вгору
            p.vy -= 8 * s * dt;

            p.x += p.vx * dt + wobbleX * dt;
            p.y += p.vy * dt + wobbleY * dt;
        }

        for (let i = spores.length - 1; i >= 0; i--) {
            if (spores[i].life <= 0) spores.splice(i, 1);
        }
    };

    const updateVeins = (dt: number) => {
        for (const v of veins) v.life -= dt * 0.85;
        for (let i = veins.length - 1; i >= 0; i--) {
            if (veins[i].life <= 0) veins.splice(i, 1);
        }
    };

    const drawSeed = (x: number, y: number, t01: number) => {
        const dx = cx - startX;
        const dy = cy - startY;
        const len = Math.hypot(dx, dy) || 1;
        const nx = dx / len;
        const ny = dy / len;

        const tailLen = lerp(26, 70, t01) * s;

        ctx.save();

        // темний слід
        ctx.globalCompositeOperation = "source-over";
        ctx.strokeStyle = "rgba(30,40,20,0.42)";
        ctx.lineWidth = 12 * s;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x - nx * tailLen, y - ny * tailLen);
        ctx.stroke();

        // гнилувато-зелена сфера
        ctx.globalCompositeOperation = "lighter";
        const r = 20 * s;
        const g = ctx.createRadialGradient(x, y, 1 * s, x, y, r);
        g.addColorStop(0, "rgba(205,255,140,0.82)");
        g.addColorStop(0.38, "rgba(110,170,70,0.50)");
        g.addColorStop(0.72, "rgba(60,90,35,0.22)");
        g.addColorStop(1, "rgba(50,70,25,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, TAU);
        ctx.fill();

        // ядро
        ctx.globalCompositeOperation = "source-over";
        ctx.fillStyle = "rgba(40,55,20,0.72)";
        ctx.beginPath();
        ctx.arc(x, y, 8 * s, 0, TAU);
        ctx.fill();

        ctx.restore();
    };

    const drawCore = (t01: number) => {
        const t = easeOutCubic(t01);
        const pulse = 0.92 + Math.sin(t01 * 16) * 0.05;
        const r = lerp(coreMax * 0.65, coreMax * 1.3, t) * pulse;

        ctx.save();

        // темне ядро
        ctx.globalCompositeOperation = "source-over";
        const dark = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 1.25);
        dark.addColorStop(0, "rgba(22,28,16,0.82)");
        dark.addColorStop(0.65, "rgba(30,38,18,0.56)");
        dark.addColorStop(1, "rgba(20,24,14,0)");
        ctx.fillStyle = dark;
        ctx.beginPath();
        ctx.arc(cx, cy, r * 1.25, 0, TAU);
        ctx.fill();

        // кислотний обідок
        ctx.globalCompositeOperation = "lighter";
        const rim = ctx.createRadialGradient(cx, cy, r * 0.2, cx, cy, r * 1.7);
        rim.addColorStop(0, "rgba(160,220,90,0)");
        rim.addColorStop(0.45, `rgba(150,210,90,${lerp(0.08, 0.24, t)})`);
        rim.addColorStop(0.75, `rgba(95,145,55,${lerp(0.16, 0.38, t)})`);
        rim.addColorStop(1, "rgba(80,120,45,0)");
        ctx.fillStyle = rim;
        ctx.beginPath();
        ctx.arc(cx, cy, r * 1.7, 0, TAU);
        ctx.fill();

        ctx.restore();
    };

    const drawMiasmaField = (t01: number) => {
        const t = easeOutCubic(t01);

        ctx.save();
        ctx.globalCompositeOperation = "source-over";

        const fog = ctx.createRadialGradient(cx, cy, 0, cx, cy, maxR);
        fog.addColorStop(0, `rgba(75,105,42,${lerp(0.08, 0.26, t)})`);
        fog.addColorStop(0.45, `rgba(40,55,24,${lerp(0.12, 0.20, t)})`);
        fog.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = fog;
        ctx.fillRect(0, 0, W, H);

        ctx.restore();
    };

    const drawShockwaves = (t01: number) => {
        const waves = [
            { delay: 0.00, radius: 0.55, width1: 28, width2: 80, alpha: 0.28 },
            { delay: 0.10, radius: 0.78, width1: 20, width2: 58, alpha: 0.22 },
            { delay: 0.22, radius: 1.00, width1: 14, width2: 40, alpha: 0.16 },
        ];

        for (const wave of waves) {
            const p = clamp01((t01 - wave.delay) / (1 - wave.delay));
            if (p <= 0) continue;

            const e = easeOutQuad(p);
            const rr = lerp(coreMax * 0.8, maxR * wave.radius, e);

            ctx.save();
            ctx.globalCompositeOperation = "source-over";
            ctx.strokeStyle = `rgba(65,95,35,${lerp(wave.alpha, 0, p)})`;
            ctx.lineWidth = lerp(wave.width1, wave.width2, p) * s;
            ctx.beginPath();
            ctx.arc(cx, cy, rr, 0, TAU);
            ctx.stroke();
            ctx.restore();

            ctx.save();
            ctx.globalCompositeOperation = "lighter";
            ctx.strokeStyle = `rgba(145,210,95,${lerp(wave.alpha * 0.65, 0, p)})`;
            ctx.lineWidth = lerp(wave.width1 * 0.18, wave.width2 * 0.12, p) * s;
            ctx.beginPath();
            ctx.arc(cx, cy, rr, 0, TAU);
            ctx.stroke();
            ctx.restore();
        }
    };

    const drawVeins = (t01: number) => {
        const power = 1 - clamp01((t01 - 0.2) / 0.7);

        ctx.save();
        for (const v of veins) {
            const a = clamp01(v.life) * power;

            ctx.globalCompositeOperation = "source-over";
            ctx.strokeStyle = `rgba(35,55,20,${0.28 * a})`;
            ctx.lineWidth = v.width * 1.9;
            ctx.lineJoin = "round";
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.moveTo(v.points[0].x, v.points[0].y);
            for (let i = 1; i < v.points.length; i++) ctx.lineTo(v.points[i].x, v.points[i].y);
            ctx.stroke();

            ctx.globalCompositeOperation = "lighter";
            ctx.strokeStyle = `rgba(135,200,90,${0.18 * a})`;
            ctx.lineWidth = v.width;
            ctx.beginPath();
            ctx.moveTo(v.points[0].x, v.points[0].y);
            for (let i = 1; i < v.points.length; i++) ctx.lineTo(v.points[i].x, v.points[i].y);
            ctx.stroke();
        }
        ctx.restore();
    };

    const drawSpores = () => {
        ctx.save();

        for (const p of spores) {
            const a = clamp01(p.life);

            // темна димка
            ctx.globalCompositeOperation = "source-over";
            ctx.fillStyle = `rgba(28,38,18,${0.12 * a})`;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 2.6, 0, TAU);
            ctx.fill();

            // зелений пил
            ctx.globalCompositeOperation = "lighter";
            ctx.fillStyle = `rgba(155,215,95,${0.18 * a})`;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, TAU);
            ctx.fill();

            // маленьке ядро
            ctx.globalCompositeOperation = "source-over";
            ctx.fillStyle = `rgba(85,120,45,${0.16 * a})`;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 0.35, 0, TAU);
            ctx.fill();
        }

        ctx.restore();
    };

    const drawScreenVignette = (t01: number) => {
        const t = easeOutCubic(t01);

        ctx.save();
        const vign = ctx.createRadialGradient(
            cx,
            cy,
            Math.min(W, H) * 0.08,
            cx,
            cy,
            Math.min(W, H) * 0.95
        );
        vign.addColorStop(0, `rgba(30,40,18,${lerp(0.02, 0.08, t)})`);
        vign.addColorStop(1, `rgba(18,22,14,${lerp(0.08, 0.24, t)})`);
        ctx.globalCompositeOperation = "source-over";
        ctx.fillStyle = vign;
        ctx.fillRect(0, 0, W, H);
        ctx.restore();
    };

    const drawBurst = (t01: number) => {
        const shakeStrength = (1 - t01) * 1.8 * s;
        const a = rng() * TAU;
        const shx = Math.cos(a) * shakeStrength;
        const shy = Math.sin(a) * shakeStrength;

        ctx.save();
        ctx.translate(shx, shy);

        drawScreenVignette(t01);
        drawMiasmaField(t01);
        drawShockwaves(t01);
        drawCore(t01);
        drawVeins(t01);
        drawSpores();

        ctx.restore();
    };

    return (time: number) => {
        const elapsed = time - startTime;
        if (elapsed < 0) return true;

        ctx.clearRect(0, 0, W, H);

        // phase 1: заражене зерно летить в центр
        if (elapsed <= FLIGHT_MS) {
            const p = clamp01(elapsed / FLIGHT_MS);
            const e = easeInOutQuad(p);

            const x = lerp(startX, cx, e);
            const y = lerp(startY, cy, e);

            drawSeed(x, y, p);
            return true;
        }

        // phase 2: розповзання чуми
        const bloomElapsed = elapsed - FLIGHT_MS;
        const p2 = clamp01(bloomElapsed / BLOOM_MS);

        if (!spawned) {
            spawnSpores();
            spawnVein(6);
            spawned = true;
        }

        if (p2 < 0.45 && rng() < 0.12) spawnVein(1);

        updateSpores(1 / 60, p2);
        updateVeins(1 / 60);
        drawBurst(p2);

        return elapsed < TOTAL_MS;
    };
};